import React, { useContext, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import Div from '@jarvis.oddle/core/components/Div'
import { Text, LinkText } from '@jarvis.oddle/core/components/Typo'
import IconArrow from '@jarvis.oddle/core/components/Icons/IconArrow'
import Flex from '@jarvis.oddle/core/components/Flex'
import IconHouse from '@jarvis.oddle/core/components/Icons/IconHouse'
import IconCalendar from '@jarvis.oddle/core/components/Icons/IconCalendar'
import Divider from '@jarvis.oddle/core/components/Divider'
import IconStore from 'src/components/icons/IconNewStore'
import IconEmail from 'src/components/icons/IconEmail'
import IconPhone from '@jarvis.oddle/core/components/Icons/IconPhone'
import { dayOfPickupOrDelivery } from 'src/utils/date'

const Container = styled(Div)`
  margin-top: 10px;
`

const StyledHeaderDetails = styled(Div)`
  display: flex;
  justify-content: space-between;
`
const StyledTextHeader = styled(Text)`
  font-weight: 700;
  font-size: 20px;
  color: ${(props) => props.theme.colorHeadingsFont};
`
const StyledIconArrow = styled(Div)`
  cursor: pointer;
`

const StyledDeliveryDetails = styled(Div)``

const StyledIcon = styled(Div)`
  margin-right: 10px;
`

const Title = styled(Text)`
  font-size: 14px;
  margin-bottom: 10px;
  color: ${(props) => props.theme.colorHeadingsFont};
`

const Section = styled(Flex)`
  margin-top: 20px;
  flex-direction: column;
`

const StyledDivider = styled(Divider)`
  margin-top: 20px;
  margin-bottom: 10px;
`
const Space = styled(Div)`
  margin-top: 10px;
`

const StyledDeliveryInstruction = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  margin-top: 20px;
  margin-bottom: 20px;
`

const StyledPhoneStore = styled(Flex)`
  margin-top: 10px;
`

const StyledLink = styled(LinkText)`
  font-weight: bold;
  color: ${(props) => props.theme.colorHeadingsFont};
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.colorHeadingsFont};
`

interface DeliveryOrPickupDetailsCardProps {
  isPickup: boolean
  phoneStore: string
  emailStore: string
  addressStore: string
  date: string
  time: string
  deliveryAddress: string
  deliveryInstructions: string
}

interface DeliveryDetailsProps {
  phoneStore: string
  emailStore: string
  addressStore: string
  date: string
  time: string
  deliveryAddress: string
  deliveryInstructions: string
}

interface PickupDetailsProps {
  phoneStore: string
  emailStore: string
  addressStore: string
  date: string
  time: string
}

const PickupDetails = ({
  phoneStore,
  emailStore,
  addressStore,
  date,
  time
}: PickupDetailsProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const day = dayOfPickupOrDelivery(date, t)
  return (
    <Div>
      <Section>
        <Title>{t('Pickup from')}</Title>
        <Flex data-testid='pickupAddressText'>
          <StyledIcon>
            <IconHouse width={16} height={16} />
          </StyledIcon>
          <StyledText bold>{addressStore}</StyledText>
        </Flex>
      </Section>
      <Section>
        <Title>{t('Pickup date')}</Title>
        <Flex data-testid='deliveryTimeText'>
          <StyledIcon>
            <IconCalendar width={19} height={19} />
          </StyledIcon>
          <StyledText bold>
            {t('_day_, _date_ _time_', {
              day,
              date,
              time: time.replace(/AM | PM/, '').replace('to', '-')
            })}
          </StyledText>
        </Flex>
        <Space />
        {emailStore && (
          <Flex data-testid='storeEmail'>
            <StyledIcon>
              <IconEmail width={18} height={13} />
            </StyledIcon>
            {
              emailStore !== '-' ? (
                <a href={`mailto:${emailStore}`}>
                  <StyledLink bold>{emailStore}</StyledLink>
                </a>
              ) : (
                <StyledLink bold>{emailStore}</StyledLink>
              )
            }
            <Space />
          </Flex>
        )}
        {phoneStore && (
          <StyledPhoneStore data-testid='storePhone'>
            <StyledIcon>
              <IconPhone width={16} height={16} />
            </StyledIcon>
            <a href={`tel:${phoneStore}`}>
              <StyledLink bold>{phoneStore}</StyledLink>
            </a>
          </StyledPhoneStore>
        )}
      </Section>
    </Div>
  )
}

const DeliveryDetails = ({
  phoneStore,
  emailStore,
  addressStore,
  date,
  time,
  deliveryInstructions,
  deliveryAddress
}: DeliveryDetailsProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const day = dayOfPickupOrDelivery(date, t)
  return (
    <StyledDeliveryDetails>
      <Section>
        <Title>{t('Delivering to')}</Title>
        <Flex data-testid='deliveryAddressText'>
          <StyledIcon>
            <IconHouse width={16} height={16} />
          </StyledIcon>
          <StyledText bold>{deliveryAddress}</StyledText>
        </Flex>
      </Section>
      <Section>
        <Title>{t('Delivery date')}</Title>
        <Flex data-testid='deliveryTimeText'>
          <StyledIcon>
            <IconCalendar width={19} height={19} />
          </StyledIcon>
          <StyledText bold>
            {t('_day_, _date_ _time_', {
              day,
              date,
              time: time.replace(/AM | PM/, '').replace('to', '-')
            })}
          </StyledText>
        </Flex>
      </Section>
      <StyledDivider />
      <Section>
        <Title>{t('Delivering from')}</Title>
        <Flex data-testid='deliveryAddressText'>
          <StyledIcon>
            <IconStore width={18} height={13} />
          </StyledIcon>
          <StyledText bold>{addressStore}</StyledText>
        </Flex>
        <Space />
        {emailStore && (
          <Flex data-testid='storeEmail'>
            <StyledIcon>
              <IconEmail width={18} height={13} />
            </StyledIcon>
            {
              emailStore !== '-' ? (
                <a href={`mailto:${emailStore}`}>
                  <StyledLink bold>{emailStore}</StyledLink>
                </a>
              ) : (
                <StyledLink bold>{emailStore}</StyledLink>
              )
            }
            <Space />
          </Flex>
        )}
        {phoneStore && (
          <StyledPhoneStore data-testid='storePhone'>
            <StyledIcon>
              <IconPhone width={16} height={16} />
            </StyledIcon>
            <a href={`tel:${phoneStore}`}>
              <StyledLink bold>{phoneStore}</StyledLink>
            </a>
          </StyledPhoneStore>
        )}
        {deliveryInstructions && (
          <>
            <StyledDeliveryInstruction data-testid='deliveryInstructionText'>
              {t('Delivery Instructions')}
            </StyledDeliveryInstruction>
            <StyledText>{deliveryInstructions}</StyledText>
          </>
        )}
      </Section>
    </StyledDeliveryDetails>
  )
}

const MobileDeliveryOrPickupDetailsCard = ({
  isPickup,
  emailStore,
  addressStore,
  phoneStore,
  date,
  time,
  deliveryAddress,
  deliveryInstructions
}: DeliveryOrPickupDetailsCardProps): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const [isCollapsable, setIscollapsable] = useState(false)
  const theme = useContext(ThemeContext)
  return (
    <Container>
      <StyledHeaderDetails
        data-testid='pickupDetailsButton'
        onClick={() => setIscollapsable(!isCollapsable)}
      >
        <StyledTextHeader>
          {isPickup ? t('Pickup Details') : t('Delivery Details')}
        </StyledTextHeader>
        <StyledIconArrow>
          <IconArrow
            iconSize='small'
            direction={isCollapsable ? 'up' : 'down'}
            fill={theme.colorHeadingsFont}
          />
        </StyledIconArrow>
      </StyledHeaderDetails>
      {isCollapsable && isPickup && (
        <PickupDetails
          emailStore={emailStore}
          phoneStore={phoneStore}
          addressStore={addressStore}
          date={date}
          time={time}
        />
      )}
      {isCollapsable && !isPickup && (
        <DeliveryDetails
          emailStore={emailStore}
          phoneStore={phoneStore}
          addressStore={addressStore}
          date={date}
          time={time}
          deliveryAddress={deliveryAddress}
          deliveryInstructions={deliveryInstructions}
        />
      )}
    </Container>
  )
}

export default MobileDeliveryOrPickupDetailsCard
